import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineX, HiOutlineTrash } from "react-icons/hi";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import FilePreviewModal, { detectPreviewKind } from "components/FilePreviewModal";
import type { FilePreviewKind } from "components/FilePreviewModal";
import ConfirmDialog from "components/ConfirmDialog";

import {
  SURVEILLANCE_ADDRESSEE_DESIGNATIONS,
  SURVEILLANCE_FORM_OF_ADDRESS_OPTIONS,
  SURVEILLANCE_CLASS_NUMBERS,
  getSurveillanceDiscussionDetails,
  submitSurveillanceApplicationDetails,
  generateSurveillanceUploadPresignedUrls,
  registerSurveillanceDocuments,
  deleteSurveillanceDocument,
  type SurveillanceApplicationInput,
} from "../../api/surveillance";
import { getLeadPrefill } from "../../api/assignment";

export const SURVEILLANCE_APPLICATION_DETAILS_FORM_ID =
  "surveillance-application-details-form";

const ACCEPTED_TYPES = ".jpg,.jpeg,.png,.webp,.pdf,.mp4";
const ACCEPTED_HELP = "JPEG, PNG, PDF, and MP4 formats, up to 50MB";
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const FILE_INPUT_ID = "surveillance-application-details-file-input";

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const REQ = <span className="text-red-500 ml-0.5">*</span>;
const INPUT_CLASS =
  "w-full min-h-[40px] md:h-9 md:min-h-0 px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";

type SavedDocument = {
  documentId: string;
  fileKey: string;
  fileName: string;
  fileUrl: string | null;
};

type EditableApplication = {
  rowId: string;
  mark: string;
  classNumber: string;
};

let rowIdCounter = 0;
function makeRowId(): string {
  rowIdCounter += 1;
  return `sv-app-row-${rowIdCounter}-${Date.now()}`;
}

function makeEmptyApplication(defaultMark = ""): EditableApplication {
  return { rowId: makeRowId(), mark: defaultMark, classNumber: "" };
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function SurveillanceApplicationDetailsFromClientView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);

  // Addressee
  const [addresseeDesignation, setAddresseeDesignation] = useState("");
  const [addresseeCustomName, setAddresseeCustomName] = useState("");
  const [addresseeFirmName, setAddresseeFirmName] = useState("");
  const [addresseePlace, setAddresseePlace] = useState("");
  const [formOfAddress, setFormOfAddress] = useState("");

  // Documents
  const [savedDocuments, setSavedDocuments] = useState<SavedDocument[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  // Applications
  const [applications, setApplications] = useState<EditableApplication[]>([
    makeEmptyApplication(),
  ]);
  const [preliminaryMark, setPreliminaryMark] = useState("");
  const [pendingDeleteRowId, setPendingDeleteRowId] = useState<string | null>(null);

  // Inline validation
  const [errors, setErrors] = useState<Set<string>>(new Set());
  const hasError = (key: string) => errors.has(key);
  const errorBorder = (key: string) =>
    hasError(key) ? " !border-red-500" : "";
  const clearError = (key: string) =>
    setErrors((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

  const [preview, setPreview] = useState<{
    url: string;
    name: string;
    kind: FilePreviewKind;
    revokeOnClose?: boolean;
  } | null>(null);

  const prefilledRef = useRef(false);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getSurveillanceDiscussionDetails(leadId)
      .then(async (details) => {
        if (cancelled) return;

        const prelimMark = details.preliminary?.markName ?? "";
        setPreliminaryMark(prelimMark);

        const d = details.applicationDetails;
        if (d) {
          const designation = d.addresseeDesignation ?? "";
          const knownDesignations = SURVEILLANCE_ADDRESSEE_DESIGNATIONS as readonly string[];
          if (designation && knownDesignations.includes(designation) && designation !== "NAME") {
            setAddresseeDesignation(designation);
          } else if (designation) {
            setAddresseeDesignation("NAME");
            setAddresseeCustomName(designation);
          }
          setAddresseeFirmName(d.addresseeFirmName ?? "");
          setAddresseePlace(d.addresseePlace ?? "");
          setFormOfAddress(d.formOfAddress ?? "");
        }

        setSavedDocuments(
          (details.documents ?? []).map((doc) => ({
            documentId: doc.documentId,
            fileKey: doc.fileKey,
            fileName: doc.filename,
            fileUrl: doc.downloadUrl ?? null,
          }))
        );

        if (details.applications.length > 0) {
          setApplications(
            details.applications.map((a) => ({
              rowId: makeRowId(),
              mark: a.mark ?? prelimMark,
              classNumber: a.classNumber != null ? String(a.classNumber) : "",
            }))
          );
        } else {
          setApplications([makeEmptyApplication(prelimMark)]);
        }

        // Prefill from reference lead when nothing has been saved yet.
        const hasSavedAppDetails = Boolean(
          d &&
            (d.addresseeDesignation ||
              d.addresseeFirmName ||
              d.addresseePlace ||
              d.formOfAddress)
        );
        const hasSavedApplications = details.applications.length > 0;
        if (
          !prefilledRef.current &&
          details.referenceLeadId &&
          !hasSavedAppDetails
        ) {
          prefilledRef.current = true;
          try {
            const prefill = await getLeadPrefill(details.referenceLeadId);
            if (cancelled) return;
            if (prefill.addressee?.formOfAddress)
              setFormOfAddress(prefill.addressee.formOfAddress);
            if (prefill.addressee?.districtOfApplicant)
              setAddresseePlace(prefill.addressee.districtOfApplicant);
            const line1 = prefill.addressee?.addresseeLine1?.trim();
            if (line1) {
              const knownDesignations =
                SURVEILLANCE_ADDRESSEE_DESIGNATIONS as readonly string[];
              if (knownDesignations.includes(line1) && line1 !== "NAME") {
                setAddresseeDesignation(line1);
              } else {
                setAddresseeDesignation("NAME");
                setAddresseeCustomName(line1);
              }
            }
            const line2 = prefill.addressee?.addresseeLine2?.trim();
            if (line2) setAddresseeFirmName(line2);
            if (
              !hasSavedApplications &&
              Array.isArray(prefill.applications) &&
              prefill.applications.length > 0
            ) {
              setApplications(
                prefill.applications.map<EditableApplication>((a) => ({
                  rowId: makeRowId(),
                  mark: a.mark ?? prelimMark ?? "",
                  classNumber:
                    a.classNumber != null ? String(a.classNumber) : "",
                }))
              );
            }
          } catch {
            // prefill is best-effort; ignore errors
          }
        }
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error ? err.message : "Failed to load application details"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const updateApplication = (
    rowId: string,
    patch: Partial<EditableApplication>
  ) => {
    setApplications((prev) =>
      prev.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row))
    );
    for (const field of Object.keys(patch)) {
      clearError(`app:${rowId}:${field}`);
    }
  };

  const addApplicationRow = () =>
    setApplications((prev) => [...prev, makeEmptyApplication(preliminaryMark)]);

  const removeApplicationRow = (rowId: string) =>
    setApplications((prev) => {
      const next = prev.filter((row) => row.rowId !== rowId);
      return next.length === 0 ? [makeEmptyApplication(preliminaryMark)] : next;
    });

  const onFilesSelected = (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (!arr.length) return;
    const accepted: File[] = [];
    for (const file of arr) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`${file.name} exceeds the 50MB limit`);
        continue;
      }
      accepted.push(file);
    }
    if (!accepted.length) return;
    setPendingFiles((prev) => [...prev, ...accepted]);
    clearError("files");
  };

  const removePendingFile = (idx: number) =>
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));

  const removeSavedDocument = async (idx: number) => {
    const doc = savedDocuments[idx];
    if (!doc || !leadId) return;
    try {
      await deleteSurveillanceDocument(leadId, doc.documentId);
      setSavedDocuments((prev) => prev.filter((_, i) => i !== idx));
      toast.success("Document removed");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to remove document"
      );
    }
  };

  const openPendingPreview = (file: File) => {
    const url = URL.createObjectURL(file);
    setPreview({
      url,
      name: file.name,
      kind: detectPreviewKind(file.name, file.type),
      revokeOnClose: true,
    });
  };

  const openSavedPreview = (doc: SavedDocument) => {
    if (!doc.fileUrl) {
      toast.error("Preview is not available for this file");
      return;
    }
    setPreview({ url: doc.fileUrl, name: doc.fileName, kind: detectPreviewKind(doc.fileName) });
  };

  const closePreview = () => {
    if (preview?.revokeOnClose) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const totalFileCount = pendingFiles.length + savedDocuments.length;

  const showAddresseeCustomName = addresseeDesignation === "NAME";

  const designationOptions = useMemo(
    () =>
      (SURVEILLANCE_ADDRESSEE_DESIGNATIONS as readonly string[]).map((d) => ({
        value: d,
        label: d === "NAME" ? "Name (Other)" : d,
      })),
    []
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;

    const next = new Set<string>();
    if (!addresseeDesignation) next.add("addresseeDesignation");
    if (addresseeDesignation === "NAME" && !addresseeCustomName.trim())
      next.add("addresseeCustomName");
    if (!formOfAddress) next.add("formOfAddress");

    for (const row of applications) {
      if (!row.mark.trim()) next.add(`app:${row.rowId}:mark`);
      if (!row.classNumber) next.add(`app:${row.rowId}:classNumber`);
    }

    if (next.size > 0) {
      setErrors(next);
      toast.error("Please fill in all required fields");
      return;
    }
    setErrors(new Set());

    setSaving(true);
    try {
      if (pendingFiles.length > 0) {
        const presigned = await generateSurveillanceUploadPresignedUrls(
          leadId,
          pendingFiles.map((file) => ({
            filename: file.name,
            mimeType: file.type || "application/octet-stream",
          }))
        );
        if (presigned.length !== pendingFiles.length) {
          throw new Error("Failed to generate upload URLs");
        }
        await Promise.all(
          pendingFiles.map(async (file, i) => {
            const target = presigned[i];
            const putRes = await fetch(target.url, {
              method: "PUT",
              body: file,
              headers: { "Content-Type": file.type || "application/octet-stream" },
            });
            if (!putRes.ok) throw new Error(`Failed to upload ${file.name}`);
          })
        );
        await registerSurveillanceDocuments(
          leadId,
          pendingFiles.map((file, i) => ({
            fileKey: presigned[i].key,
            filename: file.name,
          }))
        );
        setPendingFiles([]);
      }

      const resolvedDesignation =
        addresseeDesignation === "NAME"
          ? addresseeCustomName.trim()
          : addresseeDesignation;

      const appPayload: SurveillanceApplicationInput[] = applications
        .filter((row) => row.mark.trim())
        .map((row) => ({
          mark: row.mark.trim() || null,
          classNumber:
            row.classNumber && !isNaN(Number(row.classNumber))
              ? Number(row.classNumber)
              : null,
        }));

      await submitSurveillanceApplicationDetails(leadId, {
        addresseeDesignation: resolvedDesignation || undefined,
        addresseeFirmName: addresseeFirmName.trim() || undefined,
        addresseePlace: addresseePlace.trim() || undefined,
        formOfAddress: formOfAddress || undefined,
        applications: appPayload,
      });

      toast.success("Application details saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save application details"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading...</p>
      </div>
    );
  }

  return (
    <form
      id={SURVEILLANCE_APPLICATION_DETAILS_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* --- Addressee --- */}
      <div className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <p className="text-[14px] font-semibold text-[#101111]">Addressee</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Addressee Designation{REQ}</label>
            <Select
              value={addresseeDesignation}
              onValueChange={(v) => {
                setAddresseeDesignation(v);
                clearError("addresseeDesignation");
              }}
            >
              <SelectTrigger className={INPUT_CLASS + errorBorder("addresseeDesignation")}>
                <SelectValue placeholder="Select Designation" />
              </SelectTrigger>
              <SelectContent>
                {designationOptions.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {showAddresseeCustomName && (
            <div className="flex flex-col gap-1">
              <label className={LABEL_CLASS}>Name{REQ}</label>
              <input
                type="text"
                value={addresseeCustomName}
                onChange={(e) => {
                  setAddresseeCustomName(e.target.value);
                  clearError("addresseeCustomName");
                }}
                placeholder="Enter name"
                className={INPUT_CLASS + errorBorder("addresseeCustomName")}
              />
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Form of Address{REQ}</label>
            <Select
              value={formOfAddress}
              onValueChange={(v) => {
                setFormOfAddress(v);
                clearError("formOfAddress");
              }}
            >
              <SelectTrigger className={INPUT_CLASS + errorBorder("formOfAddress")}>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {SURVEILLANCE_FORM_OF_ADDRESS_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Firm Name</label>
            <input
              type="text"
              value={addresseeFirmName}
              onChange={(e) => setAddresseeFirmName(e.target.value)}
              placeholder="e.g. Nike India Pvt. Ltd."
              className={INPUT_CLASS}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Place</label>
            <input
              type="text"
              value={addresseePlace}
              onChange={(e) => setAddresseePlace(e.target.value)}
              placeholder="e.g. Mumbai"
              className={INPUT_CLASS}
            />
          </div>
        </div>
      </div>

      {/* --- Supporting Documents --- */}
      <div className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <div className="flex items-center justify-between">
          <p className="text-[14px] font-semibold text-[#101111]">
            Supporting Documents
          </p>
          <button
            type="button"
            disabled={saving}
            onClick={() => {
              if (saving) return;
              document.getElementById(FILE_INPUT_ID)?.click();
            }}
            className="h-9 px-3 inline-flex items-center justify-center rounded-[8px] bg-white border border-[#D5D3E0] text-[12px] font-medium text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.02)" }}
          >
            Select Files
          </button>
        </div>
        <p className="text-[11px] text-[#6D6D6D] -mt-3">{ACCEPTED_HELP}</p>

        <input
          id={FILE_INPUT_ID}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              onFilesSelected(e.target.files);
            }
            e.currentTarget.value = "";
          }}
        />

        {totalFileCount > 0 && (
          <div className="flex flex-col gap-2 self-stretch">
            {savedDocuments.map((doc, dIdx) => (
              <div
                key={`saved-${doc.documentId}`}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-[#FCFCFC]"
              >
                <span className="text-xs text-[#222222] truncate min-w-0 flex-1">
                  {doc.fileName}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => openSavedPreview(doc)}
                    className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                  >
                    Preview
                  </button>
                  {doc.fileUrl ? (
                    <a
                      href={doc.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={doc.fileName}
                      className="px-3 h-8 inline-flex items-center justify-center rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                    >
                      Download
                    </a>
                  ) : null}
                  <button
                    type="button"
                    aria-label={`Remove ${doc.fileName}`}
                    onClick={() => removeSavedDocument(dIdx)}
                    disabled={saving}
                    className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
                  >
                    <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
            {pendingFiles.map((file, pIdx) => (
              <div
                key={`pending-${pIdx}-${file.name}`}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-white"
              >
                <span className="text-xs text-[#16A34A] truncate min-w-0 flex-1">
                  {file.name}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => openPendingPreview(file)}
                    className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => removePendingFile(pIdx)}
                    disabled={saving}
                    className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
                  >
                    <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* --- Applications (grid-table layout) --- */}
      <div className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <div className="flex items-center justify-between">
          <p className="text-[14px] font-semibold text-[#101111]">Applications</p>
          <button
            type="button"
            onClick={addApplicationRow}
            className="text-sm font-normal text-[#0070F0] hover:underline"
          >
            + Add Application
          </button>
        </div>

        <div>
          <div className="flex flex-col">
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_28px] gap-3 px-1 pb-2">
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Mark Name{REQ}</div>
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Class Number{REQ}</div>
              <div />
            </div>

            {applications.map((row) => (
              <div
                key={row.rowId}
                className="flex flex-col gap-2 py-3 border-t border-[#EEEEEE] first:border-t-0"
              >
                <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_28px] gap-3 px-1 items-start">
                  <input
                    type="text"
                    value={row.mark}
                    onChange={(e) =>
                      updateApplication(row.rowId, { mark: e.target.value })
                    }
                    placeholder="e.g. Nike"
                    className={INPUT_CLASS + errorBorder(`app:${row.rowId}:mark`)}
                  />
                  <Select
                    value={row.classNumber}
                    onValueChange={(v) =>
                      updateApplication(row.rowId, { classNumber: v })
                    }
                  >
                    <SelectTrigger
                      className={INPUT_CLASS + errorBorder(`app:${row.rowId}:classNumber`)}
                    >
                      <SelectValue placeholder="Select Class" />
                    </SelectTrigger>
                    <SelectContent>
                      {SURVEILLANCE_CLASS_NUMBERS.map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center justify-center h-9">
                    {applications.length > 1 ? (
                      <button
                        type="button"
                        aria-label="Remove application"
                        onClick={() => setPendingDeleteRowId(row.rowId)}
                        className="text-[#6D6D6D] hover:text-red-600 p-1"
                      >
                        <HiOutlineTrash className="w-4 h-4" />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <FilePreviewModal
        open={!!preview}
        onClose={closePreview}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />

      <ConfirmDialog
        open={!!pendingDeleteRowId}
        title="Delete Application"
        description="Are you sure you want to delete this application? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => {
          if (pendingDeleteRowId) removeApplicationRow(pendingDeleteRowId);
          setPendingDeleteRowId(null);
        }}
        onClose={() => setPendingDeleteRowId(null)}
      />
    </form>
  );
}
